import React, { useState, useEffect } from 'react';
import Component from '../../components/Component';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Switch,
  Chip
} from '@mui/material';
import { Edit, Delete, Add, CloudUpload } from '@mui/icons-material';
import { createFeedTopic, getFeedTopics, updateFeedTopic, deleteFeedTopic } from '../../services/feedTopicsApi';

function FeedTopics() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form states
  const [formData, setFormData] = useState({
    topic_name: '',
    cid: '',
    mid: '',
    priority: '',
    options: '',
    tag: '',
    image: null,
    active: true,
    disabled: false
  });

  useEffect(() => {
    fetchTopics();
  }, []);

  const fetchTopics = async () => {
    setLoading(true);
    try {
      const response = await getFeedTopics();
      if (response.statusCode === "10000") {
        setTopics(response.data.feedTopics || []);
      } else if (response.statusCode === "10003") {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 2000);
      } else {
        showSnackbar(response.message || 'Error fetching feed topics', 'error');
        setTopics([]);
      }
    } catch (error) {
      showSnackbar('Error fetching feed topics', 'error');
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (topic = null) => {
    if (topic) {
      setEditingTopic(topic);
      setFormData({
        topic_name: topic.topic_name || '',
        cid: topic.cid || '',
        mid: topic.mid || '',
        priority: topic.priority || '',
        options: topic.options || '',
        tag: topic.tag || '',
        image: null,
        active: topic.active !== undefined ? topic.active : true,
        disabled: topic.disabled !== undefined ? topic.disabled : false
      });
    } else {
      setEditingTopic(null);
      setFormData({
        topic_name: '',
        cid: '',
        mid: '',
        priority: '',
        options: '',
        tag: '',
        image: null,
        active: true,
        disabled: false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTopic(null);
    setFormData({
      topic_name: '',
      cid: '',
      mid: '',
      priority: '',
      options: '',
      tag: '',
      image: null,
      active: true,
      disabled: false
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      image: e.target.files[0]
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let response;
      if (editingTopic) {
        response = await updateFeedTopic(editingTopic.id, formData);
      } else {
        response = await createFeedTopic(formData);
      }
      
      if (response.statusCode === "10000") {
        showSnackbar(editingTopic ? 'Feed topic updated successfully' : 'Feed topic created successfully', 'success');
        handleCloseDialog();
        fetchTopics();
      } else if (response.statusCode === "10003") {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 2000);
      } else {
        showSnackbar(response.message || 'Error saving feed topic', 'error');
      }
    } catch (error) {
      showSnackbar('Error saving feed topic', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (topicId) => {
    if (window.confirm('Are you sure you want to delete this feed topic?')) {
      setLoading(true);
      try {
        const response = await deleteFeedTopic(topicId);
        if (response.statusCode === "10000") {
          showSnackbar('Feed topic deleted successfully', 'success');
          fetchTopics();
        } else if (response.statusCode === "10003") {
          showSnackbar('Session expired. Please login again.', 'error');
          setTimeout(() => {
            window.location = '/login';
          }, 2000);
        } else {
          showSnackbar(response.message || 'Error deleting feed topic', 'error');
        }
      } catch (error) {
        showSnackbar('Error deleting feed topic', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Feed Topics Management</h3>
                </div>
                <div className="col-sm-6">
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                    sx={{ float: 'right' }}
                  >
                    Add New Feed Topic
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>ID</strong></TableCell>
                              <TableCell><strong>Topic Name</strong></TableCell>
                              <TableCell><strong>CID</strong></TableCell>
                              <TableCell><strong>MID</strong></TableCell>
                              <TableCell><strong>Priority</strong></TableCell>
                              <TableCell><strong>Image</strong></TableCell>
                              <TableCell><strong>Active</strong></TableCell>
                              <TableCell><strong>Disabled</strong></TableCell>
                              <TableCell><strong>Reactions</strong></TableCell>
                              <TableCell><strong>Actions</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {topics.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={10} align="center">
                                  <Typography variant="body1" color="text.secondary">
                                    No feed topics found
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              topics.map((topic) => (
                                <TableRow key={topic.id}>
                                  <TableCell>{topic.id}</TableCell>
                                  <TableCell>{topic.topic_name}</TableCell>
                                  <TableCell>{topic.cid || '-'}</TableCell>
                                  <TableCell>{topic.mid || '-'}</TableCell>
                                  <TableCell>{topic.priority || '-'}</TableCell>
                                  <TableCell>
                                    {topic.image_url ? (
                                      <img 
                                        src={topic.image_url} 
                                        alt={topic.topic_name} 
                                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                      />
                                    ) : (
                                      '-'
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={topic.active ? 'Yes' : 'No'} 
                                      color={topic.active ? 'success' : 'default'} 
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={topic.disabled ? 'Yes' : 'No'} 
                                      color={topic.disabled ? 'error' : 'default'} 
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {topic.reactions_count || '0'}
                                  </TableCell>
                                  <TableCell>
                                    <IconButton
                                      color="primary"
                                      onClick={() => handleOpenDialog(topic)}
                                      size="small"
                                    >
                                      <Edit />
                                    </IconButton>
                                    <IconButton
                                      color="error"
                                      onClick={() => handleDelete(topic.id)}
                                      size="small"
                                    >
                                      <Delete />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingTopic ? 'Edit Feed Topic' : 'Add New Feed Topic'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="topic_name"
                    label="Topic Name"
                    value={formData.topic_name}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="cid"
                    label="CID (Competition ID)"
                    type="number"
                    value={formData.cid}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="mid"
                    label="MID (Match ID)"
                    type="number"
                    value={formData.mid}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="priority"
                    label="Priority"
                    type="number"
                    value={formData.priority}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="options"
                    label='Options (e.g. ["PAK","SL"])'
                    value={formData.options}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="tag"
                    label="Tag (e.g. Trending)"
                    value={formData.tag}
                    onChange={handleInputChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="active"
                        checked={formData.active}
                        onChange={handleSwitchChange}
                        color="success"
                      />
                    }
                    label="Active"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="disabled"
                        checked={formData.disabled}
                        onChange={handleSwitchChange}
                        color="error"
                      />
                    }
                    label="Disabled"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    fullWidth
                    sx={{ py: 2 }}
                  >
                    {formData.image ? formData.image.name : 'Upload Topic Image'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFileChange}
                      required={!editingTopic}
                    />
                  </Button>
                  {editingTopic && editingTopic.image_url && !formData.image && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Current Image:
                      </Typography>
                      <img 
                        src={editingTopic.image_url} 
                        alt="Current" 
                        style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={20} /> : (editingTopic ? 'Update' : 'Create')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

export default FeedTopics;

